import React, { useEffect, useState } from 'react';
import { useTheme, Box, Typography, CircularProgress, alpha, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider, Chip } from '@mui/material';
import { fetchProducts } from '../api';
import { fetchOffers } from '../offersApi';
import { useCurrency } from '../CurrencyContext';
import { getCurrencyDecimals } from '../currencyHelpers';
import { getImageUrls } from '../getImageUrls';
import { useTranslation } from 'react-i18next';
import PaymentIcon from '@mui/icons-material/Payment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const getItems = (order) => {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) return order.items;
  if (order?.OrderItemsJson) { try { return JSON.parse(order.OrderItemsJson); } catch {} }
  return [];
};

const PrePaymentProcessingPayment = ({ order, stageName }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const items = getItems(order);
  const [productMap, setProductMap] = useState({});
  
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const orderCurrency = order?.Currency || selectedCurrency || 'YER';
  const currencySymbol = getCurrencySymbol(orderCurrency);
  const decimals = getCurrencyDecimals(orderCurrency);
  
  const formatPrice = (amount) => {
    const num = Number(amount || 0);
    return `${num.toFixed(decimals)} ${currencySymbol}`;
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchProducts(), fetchOffers()]).then(([products, offers]) => {
      const allOfferProducts = (offers || []).flatMap(offer => 
        (offer.products || []).map(prod => ({ 
          ...prod, 
          Id: `${offer.offerCategoryId}_${prod.id}`, 
          ProductId: prod.id, 
          ProductName: prod.productName, 
          Price: prod.price, 
          ImageUrl: prod.imageJson,
          Description: prod.description,
          ProductColors: prod.ProductColors || prod.productColors || []
        }))
      );
      const allProducts = [...products, ...allOfferProducts];
      const map = {};
      allProducts.forEach(p => { 
        map[String(p.Id)] = p; 
        if (p.ProductCode) map[String(p.ProductCode)] = p;
        if (p.ProductId) map[String(p.ProductId)] = p;
      });
      if (mounted) setProductMap(map);
    });
    return () => { mounted = false; };
  }, []);

  return (
    <Box sx={{ 
      maxWidth: 650, 
      margin: '2rem auto', 
      background: alpha(theme.palette.background.paper, 0.95),
      backdropFilter: 'blur(12px)', 
      borderRadius: '28px', 
      boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`, 
      p: { xs: 3, md: 4 },
      border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 6,
        background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`
      }
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3,
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.2)} 0%, transparent 70%)`,
            animation: 'pulse 2s infinite'
          }} />
          <PaymentIcon sx={{ 
            fontSize: 80, 
            color: 'warning.main',
            position: 'relative',
            zIndex: 1,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
          }} />
        </Box>
        
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          fontWeight: 900,
          mb: 2,
          color: 'warning.main',
          textAlign: 'center',
          letterSpacing: '-0.5px'
        }}>
          {stageName || t('pending.pending.title')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={24} sx={{ color: 'warning.main' }} />
          <Chip 
            label={t('prePayment.processingPayment.message', 'Verifying your payment...')} 
            color="warning" 
            sx={{ 
              fontWeight: 700,
              fontSize: '0.95rem',
              px: 3,
              py: 2.5,
              borderRadius: '16px',
              boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
            }} 
          />
        </Box>
      </Box>

      {/* Message */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 3,
        p: 2.5,
        borderRadius: '16px',
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
        position: 'relative',
        zIndex: 1
      }}>
        <AccessTimeIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" color="warning.main" gutterBottom sx={{ fontWeight: 700 }}>
          {t('prePayment.processingPayment.message', 'Verifying your payment...')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('prePayment.processingPayment.description', 'Please wait while we verify your payment. This usually takes a few minutes.')}
        </Typography>
      </Box>

      {/* Order Info */}
      <Box sx={{ 
        bgcolor: alpha(theme.palette.warning.main, 0.05),
        borderRadius: '20px',
        p: 3,
        mb: 3,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">
            {t('orderStages.common.orderNumber')}:
          </Typography>
          <Chip 
            label={`#${order?.OrderId}`} 
            size="small" 
            sx={{ fontFamily: 'monospace', fontWeight: 800, bgcolor: 'background.paper', px: 2 }} 
          />
        </Box>
        <Divider sx={{ my: 1.5, opacity: 0.6 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">
            {t('orderStages.common.customer')}:
          </Typography>
          <Typography variant="body1" fontWeight="600">
            {order?.CustomerName}
          </Typography>
        </Box>
        <Divider sx={{ my: 1.5, opacity: 0.6 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">
            {t('cart.total')}:
          </Typography>
          <Typography variant="h6" fontWeight="900" color="warning.main">
            {formatPrice(order?.TotalAmount)}
          </Typography>
        </Box>
      </Box>

      {/* Items List */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderRadius: '20px',
        p: 3,
        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
        position: 'relative',
        zIndex: 1
      }}>
        <Typography variant="h6" sx={{ 
          mb: 2,
          fontWeight: 900,
          borderBottom: '3px solid',
          borderColor: 'warning.main',
          pb: 1.5,
          color: 'text.primary'
        }}>
          {t('orderStages.common.items')}
        </Typography>
        
        <List disablePadding>
          {items.length === 0 && (
            <Typography color="text.secondary">
              {t('orderStages.common.noItems')}
            </Typography>
          )}
          
          {items.map((item, idx) => {
            const code = item.ProductCode || item.ProductId || item.id || item.Id;
            let prod = productMap[String(code)] || {};
            
            if (Object.keys(prod).length === 0 && item.ProductId) {
              prod = Object.values(productMap).find(p => 
                String(p.ProductId) === String(item.ProductId) ||
                String(p.Id) === String(item.ProductId)
              ) || {};
            }
            
            const mergedProduct = { ...prod, ...item };
            const name = item.ProductName || item.name || prod.ProductName || prod.Name || code;
            const description = item.Description || item.description || prod.Description || '';
            const { thumbnail: img } = getImageUrls(mergedProduct);
            const qty = item.Quantity || item.quantity || 1;
            const price = Number(item.Price || item.price || 0);
            const total = qty * price;
            const selectedColor = item.SelectedColor || item.selectedColor || null;
            
            return (
              <React.Fragment key={idx}>
                <ListItem alignItems="flex-start" sx={{ 
                  px: 0, 
                  py: 2,
                  '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.03) },
                  transition: 'background 0.2s',
                  borderRadius: '12px'
                }}>
                  <ListItemAvatar>
                    <Avatar 
                      variant="rounded" 
                      src={img} 
                      alt={name}
                      sx={{ 
                        width: 60,
                        height: 60,
                        mr: 2,
                        border: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`
                      }}
                    >
                      {name?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="800">
                            {name}
                          </Typography>
                          <Chip 
                            label={`×${qty}`} 
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.75rem',
                              fontWeight: 700
                            }}
                          />
                        </Box>
                        
                        {selectedColor && (
                          <Chip 
                            label={`🎨 ${selectedColor}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ 
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              alignSelf: 'flex-start'
                            }}
                          />
                        )}
                        
                        {description && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.75rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" fontWeight="600">
                        {formatPrice(price)} {t('orderStages.common.each', 'each')}
                      </Typography>
                    }
                  />
                  
                  <Typography variant="subtitle1" fontWeight="900" color="warning.main" sx={{ fontSize: '1.1rem' }}>
                    {formatPrice(total)}
                  </Typography>
                </ListItem>
                
                {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                  <Box sx={{ 
                    px: 1,
                    pb: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.03),
                    borderRadius: '12px',
                    mb: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 800,
                      color: 'info.main',
                      mb: 1,
                      fontSize: '0.85rem'
                    }}>
                      {t('customProduct.selectedSubproducts', 'Selected Options')}:
                    </Typography>
                    <List dense disablePadding>
                      {item.SubProducts.map((sp, spIdx) => {
                        if (!sp.Name) return null;
                        const spPrice = Number(sp.Price || sp.price || 0);
                        
                        return (
                          <ListItem key={sp.Id || spIdx} sx={{ pl: 0, py: 0.5 }}>
                            <ListItemText primary={
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                                  {sp.Name}
                                </Typography>
                                {spPrice > 0 && (
                                  <Chip 
                                    label={formatPrice(spPrice)} 
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            } />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
                
                {idx < items.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>
    </Box>
  );
};

export default PrePaymentProcessingPayment;